/*
  RHP Bundle Cart
  ------------------------------------------------------------
  Two jobs:

  1. ENFORCE — a bundle is one unit. The cart holds it as three
     real jar lines so inventory decrements per flavor, which
     means any cart UI will happily let someone delete a jar or
     bump a quantity. This watches the cart and puts it back.

  2. DISPLAY — merge those lines into one bundle card in Halo's
     cart page and drawer. Cosmetic, and it only knows Halo's
     markup. Third-party drawers (Opus) render their own DOM and
     are left alone — enforcement still covers them, which is
     the part that actually matters.

  Intended composition rides on every line as
  _rhp_bundle_map = "variantId:qty,variantId:qty".
*/
(function () {
  'use strict';

  var GROUP_KEY = '_rhp_bundle_id';
  var MAP_KEY   = '_rhp_bundle_map';
  var LABEL_KEY = 'Bundle';

  var COMPONENTS = (window.RHP_BUNDLE_COMPONENTS || []).map(Number);

  var busy = false;
  var decorating = false;
  var repairs = 0;
  var MAX_REPAIRS = 6;
  var lastState = '';

  function setText(el, v) { if (el && el.textContent !== v) el.textContent = v; }

  function money(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.RHP_MONEY_FORMAT || '${{amount}}');
      }
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  function post(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
  }

  function parseMap(str) {
    var out = {};
    if (!str) return out;
    str.split(',').forEach(function (pair) {
      var kv = pair.split(':');
      if (kv.length === 2 && kv[0] && kv[1]) out[String(Number(kv[0]))] = parseInt(kv[1], 10);
    });
    return out;
  }

  function stateHash(cart) {
    return cart.items.map(function (i) {
      return i.variant_id + 'x' + i.quantity + '@' + ((i.properties || {})[GROUP_KEY] || '');
    }).join('|');
  }

  function toast(msg) {
    var el = document.querySelector('.rhp-bcart-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'rhp-bcart-toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.classList.add('is-on');
    clearTimeout(el._t);
    el._t = setTimeout(function () { el.classList.remove('is-on'); }, 4500);
  }

  /* Tell whichever cart UI is on screen to redraw. */
  function notify() {
    ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (n) {
      document.dispatchEvent(new CustomEvent(n, { bubbles: true, detail: { source: 'rhp-bundle' } }));
    });
    try {
      if (window.Shopify && typeof window.Shopify.getCart === 'function' &&
          window.halo && typeof window.halo.updateSidebarCart === 'function') {
        window.Shopify.getCart(function (c) { window.halo.updateSidebarCart(c); });
      }
    } catch (e) {}
    setTimeout(decorate, 300);
  }

  /* ---------------------------------------------------------
     1. ENFORCE
     --------------------------------------------------------- */
  function enforce(cart) {
    if (busy || repairs >= MAX_REPAIRS) return Promise.resolve(false);

    var groups = {};
    var looseKeys = [];

    cart.items.forEach(function (item) {
      var p = item.properties || {};
      var bid = p[GROUP_KEY];
      if (bid) {
        if (!groups[bid]) {
          groups[bid] = { map: p[MAP_KEY] || '', props: p, lines: {} };
        }
        groups[bid].lines[String(item.variant_id)] = { key: item.key, qty: item.quantity };
      } else if (COMPONENTS.length && COMPONENTS.indexOf(item.variant_id) !== -1) {
        // A component jar loose in the cart. These are not sold on their own.
        looseKeys.push(item.key);
      }
    });

    var updates = {};
    var readds = [];
    var allGroupKeys = [];
    var changed = false;

    Object.keys(groups).forEach(function (bid) {
      var g = groups[bid];
      Object.keys(g.lines).forEach(function (vid) { allGroupKeys.push(g.lines[vid].key); });

      var want = parseMap(g.map);
      if (!Object.keys(want).length) return;   // no map recorded — leave it alone

      Object.keys(want).forEach(function (vid) {
        var line = g.lines[vid];
        if (!line) {
          var props = {};
          Object.keys(g.props).forEach(function (k) { props[k] = g.props[k]; });
          readds.push({ id: Number(vid), quantity: want[vid], properties: props });
          changed = true;
        } else if (line.qty !== want[vid]) {
          updates[line.key] = want[vid];
          changed = true;
        }
      });

      Object.keys(g.lines).forEach(function (vid) {
        if (!(vid in want)) { updates[g.lines[vid].key] = 0; changed = true; }
      });
    });

    looseKeys.forEach(function (k) { updates[k] = 0; changed = true; });

    if (!changed) return Promise.resolve(false);

    busy = true;
    repairs++;

    var chain = Promise.resolve();

    if (Object.keys(updates).length) {
      chain = chain.then(function () { return post('/cart/update.js', { updates: updates }); });
    }

    if (readds.length) {
      chain = chain.then(function () {
        return post('/cart/add.js', { items: readds }).then(function (r) {
          if (r.ok) return;
          // A jar sold out, so the bundle can't be rebuilt. Drop it rather
          // than let a partial bundle reach checkout.
          var kill = {};
          allGroupKeys.forEach(function (k) { kill[k] = 0; });
          return post('/cart/update.js', { updates: kill }).then(function () {
            toast('One of those flavors just sold out, so the bundle was removed.');
          });
        });
      });
    }

    return chain
      .then(function () { notify(); return true; })
      .catch(function (e) { console.error('[rhp-bundle-cart] repair failed', e); return false; })
      .finally(function () { busy = false; });
  }

  /* ---------------------------------------------------------
     2. DISPLAY — Halo cart page and Halo drawer only
     --------------------------------------------------------- */
  function rowForKey(key) {
    var rows = document.querySelectorAll('[data-item-row]');
    for (var i = 0; i < rows.length; i++) {
      if (rows[i].dataset.line === key) return rows[i];
      if (rows[i].querySelector('[data-line="' + CSS.escape(key) + '"]')) return rows[i];
    }
    return null;
  }

  function removeBundle(keys) {
    if (busy) return;
    busy = true;
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    post('/cart/update.js', { updates: updates })
      .then(function () {
        if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
        notify();
      })
      .catch(function (e) { console.error('[rhp-bundle-cart]', e); })
      .finally(function () { busy = false; });
  }

  function buildHeader(group) {
    var head = document.createElement('div');
    head.className = 'rhp-bcart__head';

    var left = document.createElement('div');
    left.className = 'rhp-bcart__head-left';
    var title = document.createElement('div');
    title.className = 'rhp-bcart__title';
    title.textContent = group.label;
    var meta = document.createElement('div');
    meta.className = 'rhp-bcart__meta';
    meta.textContent = group.count + (group.count === 1 ? ' jar' : ' jars');
    left.appendChild(title); left.appendChild(meta);

    var right = document.createElement('div');
    right.className = 'rhp-bcart__head-right';
    var price = document.createElement('div');
    price.className = 'rhp-bcart__price';
    price.textContent = money(group.total);
    var remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'rhp-bcart__remove';
    remove.setAttribute('aria-label', 'Remove ' + group.label);
    remove.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    remove.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation(); removeBundle(group.keys);
    });
    right.appendChild(price); right.appendChild(remove);

    head.appendChild(left); head.appendChild(right);
    return head;
  }

  function decorate() {
    if (decorating) return;
    decorating = true;

    getCart().then(function (cart) {
      if (!cart || !cart.items) return;

      var hash = stateHash(cart);
      if (hash !== lastState) {
        lastState = hash;
        enforce(cart).then(function (fixed) {
          if (fixed) toast('Bundles are sold as a set, so your bundle was put back.');
        });
      }

      var order = [], groups = {};
      cart.items.forEach(function (item) {
        var p = item.properties || {};
        var id = p[GROUP_KEY];
        if (!id) return;
        if (!groups[id]) {
          groups[id] = { id: id, label: p[LABEL_KEY] || 'Bundle', keys: [], total: 0, count: 0 };
          order.push(id);
        }
        groups[id].keys.push(item.key);
        groups[id].total += item.final_line_price;
        groups[id].count += item.quantity;
      });

      document.querySelectorAll('.rhp-bcart').forEach(function (w) {
        if (!groups[w.dataset.bundleId] || !document.contains(w)) {
          var parent = w.parentNode; if (!parent) return;
          while (w.querySelector('[data-item-row]')) parent.insertBefore(w.querySelector('[data-item-row]'), w);
          w.remove();
        }
      });

      order.forEach(function (id) {
        var group = groups[id];
        var existing = document.querySelector('.rhp-bcart[data-bundle-id="' + CSS.escape(id) + '"]');
        var rows = group.keys.map(rowForKey).filter(Boolean);
        if (!rows.length) return;

        if (existing && existing.querySelectorAll('[data-item-row]').length === rows.length) {
          setText(existing.querySelector('.rhp-bcart__price'), money(group.total));
          setText(existing.querySelector('.rhp-bcart__meta'),
                  group.count + (group.count === 1 ? ' jar' : ' jars'));
          return;
        }

        var wrap = existing;
        if (!wrap) {
          wrap = document.createElement('div');
          wrap.className = 'rhp-bcart';
          wrap.dataset.bundleId = id;
          rows[0].parentNode.insertBefore(wrap, rows[0]);
          wrap.appendChild(buildHeader(group));
        }
        var bodyEl = wrap.querySelector('.rhp-bcart__body');
        if (!bodyEl) {
          bodyEl = document.createElement('div');
          bodyEl.className = 'rhp-bcart__body';
          wrap.appendChild(bodyEl);
        }
        rows.forEach(function (row) { row.classList.add('rhp-bcart__line'); bodyEl.appendChild(row); });
      });
    }).catch(function (e) {
      console.error('[rhp-bundle-cart]', e);
    }).finally(function () {
      setTimeout(function () { decorating = false; }, 60);
    });
  }

  /* ---------------------------------------------------------
     wiring
     --------------------------------------------------------- */
  var pending = null;
  function schedule() { clearTimeout(pending); pending = setTimeout(decorate, 200); }

  function watch() {
    var obs = new MutationObserver(function (records) {
      if (decorating || busy) return;
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) { schedule(); return; }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });

    // Any cart write on the page re-runs the check, whichever UI made it —
    // theme, app drawer, or another script.
    var of = window.fetch;
    if (typeof of === 'function' && !window.__rhpFetchPatched) {
      window.__rhpFetchPatched = true;
      window.fetch = function () {
        var u = arguments[0];
        var href = typeof u === 'string' ? u : (u && u.url) || '';
        var isCart = /\/cart\/(add|change|update|clear)(\.js)?/.test(href);
        return of.apply(this, arguments).then(function (res) {
          if (isCart && !busy) schedule();
          return res;
        });
      };
    }

    ['cart:refresh', 'cart:updated', 'cart:change'].forEach(function (n) {
      document.addEventListener(n, function (e) {
        if (e.detail && e.detail.source === 'rhp-bundle') return;
        schedule();
      });
    });
    document.addEventListener('rhp:bundle:added', schedule);
  }

  function init() { decorate(); watch(); }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
